import { TEST_TYPES } from "enums/BirthTypes";
import useAuth from "hooks/useAuth";
import React, {
  createContext, useCallback, useEffect, useMemo, useState,
} from "react";
import { toast } from "react-toastify";
import AxiosService from "services/Axios";

const FortuneContext = createContext();
const CURRENT_FORTUNE_YEAR = new Date().getFullYear();

const FortuneProvider = ({ children }) => {
  const [fortuneData, setFortuneData] = useState({
    [TEST_TYPES.YEAR]: null,
    [TEST_TYPES.DAILY]: null,
    [TEST_TYPES.DOB]: null,
  });
  const fortuneDataRef = React.useRef(fortuneData);

  useEffect(() => {
    fortuneDataRef.current = fortuneData;
  }, [fortuneData]);

  const { user } = useAuth();

  const getFortuneUrl = (testType) => {
    switch (testType) {
      case TEST_TYPES.YEAR:
        return `${process.env.REACT_APP_API_URL}/user/fortune/${CURRENT_FORTUNE_YEAR}`;
      case TEST_TYPES.DAILY:
        return `${process.env.REACT_APP_API_URL}/user/fortune/daily`;
      case TEST_TYPES.LOTTO_DRAW:
        return `${process.env.REACT_APP_API_URL}/user/fortune/lotto-draw`;
      case TEST_TYPES.DOB:
      default:
        return `${process.env.REACT_APP_API_URL}/user/fortune`;
    }
  };

  const getFortune = useCallback(async (testType, force = false) => {
    // Check if the fortune data already exists for the requested testType
    if (fortuneDataRef.current[testType] && !force) {
      return; // If the data already exists, don't make the API call
    }

    try {
      const url = getFortuneUrl(testType);
      const res = await AxiosService.get(url);
      setFortuneData((prevData) => ({
        ...prevData,
        [testType]: res.data.responseObject,
      }));
    } catch (error) {
      toast.error("데이터를 불러오는데 오류가 발생했습니다.", error);
    }
  }, []);

  const getNameFortuneNumbers = async (formData) => {
    try {
      return await AxiosService.post(`${process.env.REACT_APP_API_URL}/user/fortune/name/code`, formData);
    } catch (error) {
      toast.error("데이터를 불러오는데 오류가 발생했습니다.", error);
    }
  };

  const getNameFortuneNumbersResult = async (formData) => {
    try {
      return await AxiosService.post(`${process.env.REACT_APP_API_URL}/user/fortune/name/result`, formData);
    } catch (error) {
      toast.error("데이터를 불러오는데 오류가 발생했습니다.", error);
    }
  };

  const getFreeTotalFortuneResult = async (formData) => AxiosService.post(`${process.env.REACT_APP_API_URL}/user/fortune/total`, formData);

  const updateDailyFortuneResult = useCallback((newResult) => {
    setFortuneData((prev) => {
      const prevDaily = prev[TEST_TYPES.DAILY] || {};
      const prevResult = prevDaily.result || {};

      return {
        ...prev,
        [TEST_TYPES.DAILY]: {
          ...prevDaily,
          result: {
            ...prevResult,
            ...newResult,
          },
        },
      };
    });
  }, []);

  useEffect(() => {
    if (user?.name && user?.dob) {
      [TEST_TYPES.DOB, TEST_TYPES.YEAR, TEST_TYPES.DAILY, TEST_TYPES.LOTTO_DRAW].forEach(getFortune);
    }
  }, [user, getFortune]);

  const contextValue = useMemo(() => ({
    fortune2026: fortuneData[TEST_TYPES.YEAR],
    dailyTest: fortuneData[TEST_TYPES.DAILY],
    fortune: fortuneData[TEST_TYPES.DOB],
    lottoDraw: fortuneData[TEST_TYPES.LOTTO_DRAW],
    getNameFortuneNumbers,
    getNameFortuneNumbersResult,
    getFortune,
    updateDailyFortuneResult,
    getFreeTotalFortuneResult,
  }), [
    fortuneData,
    getNameFortuneNumbers,
    getNameFortuneNumbersResult,
    getFortune,
    updateDailyFortuneResult,
    getFreeTotalFortuneResult,
  ]);

  return (
    <FortuneContext.Provider
      value={contextValue}
    >
      {children}
    </FortuneContext.Provider>
  );
};

export { FortuneContext, FortuneProvider };
